import React from 'react';
import { useIntl } from 'react-intl';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TRPCClientErrorLike } from '@trpc/client';
import { DefaultErrorShape } from '@trpc/server/unstable-core-do-not-import';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { faChartLine } from '@fortawesome/pro-light-svg-icons/faChartLine';
import { TypedMemo } from '../../../common/types';
import { ChartSkeleton } from '../ChartSkeleton/ChartSkeleton';
import { useChartTokens } from '../../theme/useChartTokens';
import { Panel, PanelBody, PanelHead } from '../primitives/Panel';

interface PositionData {
  date: string;
  position: number;
}

interface DateRange {
  start: string;
  end: string;
}

interface ChangePoint {
  date: string;
  magnitude: number;
}

interface PositionChangeChartProps {
  title: string;
  data: PositionData[];
  dateRange?: DateRange;
  isLoading: boolean;
  error: TRPCClientErrorLike<{ transformer: true; errorShape: DefaultErrorShape }> | null;
  changePoints?: ChangePoint[];
}

const PositionChangeChartComponent: React.FC<PositionChangeChartProps> = ({
  title,
  data,
  dateRange,
  isLoading,
  error,
  changePoints = [],
}) => {
  const intl = useIntl();
  const chart = useChartTokens();

  // Calculate Y-axis domain for better visualization
  const yAxisDomain = React.useMemo(() => {
    if (data.length === 0) return ['auto', 'auto'];

    const positions = data.map((d) => d.position);
    const minPosition = Math.min(...positions);
    const maxPosition = Math.max(...positions);

    // Add some padding around the actual range
    const padding = Math.max(1, (maxPosition - minPosition) * 0.1);
    const domainMin = Math.max(1, Math.floor(minPosition - padding));
    const domainMax = Math.ceil(maxPosition + padding);

    return [domainMin, domainMax];
  }, [data]);

  const formatTooltipValue = (value: number, name: string) => {
    if (name === 'position') {
      return [`#${value}`, 'Position'];
    }
    return [value, name];
  };

  const formatDateForTooltip = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatXAxisDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
  };

  // Format date range for display
  const dateRangeText = React.useMemo(() => {
    if (dateRange === undefined) return '';

    const startFormatted = new Date(dateRange.start).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
    });
    const endFormatted = new Date(dateRange.end).toLocaleDateString(intl.locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${startFormatted} - ${endFormatted}`;
  }, [dateRange, intl.locale]);

  return (
    <Panel className="flex-grow-1" style={{ minHeight: '318px' }}>
      <PanelHead title={title} subtitle={dateRangeText} />
      <PanelBody>
        {isLoading ? (
          <div style={{ height: '250px' }}>
            <ChartSkeleton />
          </div>
        ) : error !== null && error !== undefined ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon
                icon={faTriangleExclamation}
                className="mb-2"
                style={{ fontSize: '2rem', color: chart.neg }}
              />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.error',
                  defaultMessage: 'Error loading chart data',
                })}
              </div>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ height: '250px', color: chart.tx2 }}
          >
            <div className="text-center">
              <FontAwesomeIcon icon={faChartLine} className="mb-2" style={{ fontSize: '2rem' }} />
              <div style={{ fontSize: '14px' }}>
                {intl.formatMessage({
                  id: 'app.sml.chart.no-data',
                  defaultMessage: 'No data available for this period',
                })}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: '250px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{
                  top: 10,
                  right: 10,
                  left: 10,
                  bottom: 10,
                }}
              >
                <CartesianGrid strokeDasharray="2 5" stroke={chart.grid} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: chart.label }}
                  tickFormatter={formatXAxisDate}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                />
                <YAxis
                  reversed // Reverse Y-axis so position 1 is at the top
                  domain={yAxisDomain}
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: chart.axis }}
                  axisLine={{ stroke: chart.grid }}
                  tickLine={{ stroke: chart.grid }}
                  tickFormatter={(value) => `#${value}`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: chart.tooltipBg,
                    border: `1px solid ${chart.line}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    color: chart.tooltipTx,
                  }}
                  labelFormatter={formatDateForTooltip}
                  formatter={formatTooltipValue}
                />
                {changePoints.map((cp) => (
                  <ReferenceLine
                    key={cp.date}
                    x={cp.date}
                    stroke={chart.brand}
                    strokeDasharray="4 4"
                    aria-label={`Change point ${cp.date}`}
                  />
                ))}
                <Line
                  type="monotone"
                  dataKey="position"
                  stroke={chart.pos}
                  strokeWidth={2.2}
                  dot={{
                    fill: chart.pos,
                    strokeWidth: 1,
                    r: 4,
                  }}
                  activeDot={{
                    r: 6,
                    stroke: chart.pos,
                    strokeWidth: 1,
                    fill: chart.dotCore,
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </PanelBody>
    </Panel>
  );
};

export const PositionChangeChart = TypedMemo(PositionChangeChartComponent);
